import { useMemo, type KeyboardEvent, type MouseEvent } from 'react';

import { UnstyledButtonLink, UnstyledButton } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import dt from '~/testing';

import useStyles from './SubHeader.styles';
import type { SubMenuItem, SubNavVariant } from './types';

type SubNavLinkProps = SubMenuItem & {
  subNavVariant?: SubNavVariant;
  role?: 'tab';
  id?: string;
  ariaControls?: string;
  tabIndex?: number;
  className?: string;
  onKeyDown?: (event: KeyboardEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
};

function SubNavLink({
  href,
  label,
  isActive,
  isDisabled,
  hasBadge,
  onClick,
  value,
  dataTestId,
  subNavVariant,
  role,
  id,
  ariaControls,
  tabIndex,
  className,
  onKeyDown,
}: SubNavLinkProps) {
  const { classes, cx } = useStyles({ subNavVariant });
  const ButtonComponent = useMemo(() => (href ? UnstyledButtonLink : UnstyledButton), [href]);

  return (
    <ButtonComponent
      className={cx(classes.subNavLink, isActive && 'active', isDisabled && 'disabled', className)}
      href={href}
      type={href ? undefined : 'button'}
      onClick={(event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        if (href && isActive) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        onClick?.(value);
      }}
      onKeyDown={onKeyDown}
      role={role}
      id={id}
      tabIndex={tabIndex}
      aria-controls={ariaControls}
      aria-selected={role === 'tab' ? Boolean(isActive) : undefined}
      aria-current={role === undefined && isActive ? 'page' : undefined}
      data-test-id={dataTestId || dt.common.components.appShellSubheader.subHeaderLink}
    >
      <Text variant="headline-small">{label}</Text>
      {hasBadge && <div className={classes.badge} />}
    </ButtonComponent>
  );
}

export default SubNavLink;
